import type {
  AnalyseResponse,
  AnalysisResponse,
  ChartResponse,
  NewsOverview,
  NewsSymbolResponse,
  OptionChainResponse,
  OptionPreset,
  OptionPresetId,
  VolatilityResponse,
  BacktestResult,
  BacktestRunResponse,
  CandleStoreResponse,
  SavedBacktestRun,
  SignificanceReport,
  WalkForwardResult,
  CycleSummary,
  DashboardResponse,
  Decision,
  EquityResponse,
  ModelBudgetResponse,
  ModelComparisonRow,
  ModelsResponse,
  ProvidersResponse,
  Quote,
  RunCycleResponse,
  SettingsResponse,
  StreamKind,
  TradesResponse,
  UniverseEntry,
} from './types';

/**
 * Thrown for any non-2xx response so callers can show the server's own message
 * rather than a generic failure string.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isUnauthorised(): boolean {
    return this.status === 401;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`/api${path}`, {
    ...init,
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init.headers ?? {}),
    },
    // The session lives in an httpOnly cookie, so it must be sent along.
    credentials: 'same-origin',
  });

  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }

  if (!response.ok) {
    const message =
      payload && typeof payload === 'object' && 'error' in payload
        ? String((payload as { error: unknown }).error)
        : `Request failed with status ${response.status}`;
    throw new ApiError(response.status, message);
  }

  return payload as T;
}

const post = <T>(path: string, body?: unknown): Promise<T> =>
  request<T>(path, { method: 'POST', ...(body === undefined ? {} : { body: JSON.stringify(body) }) });

export const api = {
  // --- auth ---
  authStatus: () =>
    request<{ provisioned: boolean; username: string; ephemeralSessionSecret: boolean }>(
      '/auth/status',
    ),
  login: (username: string, password: string) =>
    post<{ username: string; expiresAt: string }>('/auth/login', { username, password }),
  logout: () => post<{ ok: boolean }>('/auth/logout'),
  me: () => request<{ username: string; lastLoginAt: string | null }>('/auth/me'),

  // --- dashboard ---
  dashboard: () => request<DashboardResponse>('/dashboard'),
  equity: () => request<EquityResponse>('/dashboard/equity'),
  cycles: (limit = 40) => request<{ cycles: CycleSummary[] }>(`/dashboard/cycles?limit=${limit}`),
  decisions: (
    params: { limit?: number; streamId?: number; action?: string; accepted?: boolean; symbol?: string } = {},
  ) => {
    const query = new URLSearchParams();
    if (params.limit) query.set('limit', String(params.limit));
    if (params.streamId) query.set('streamId', String(params.streamId));
    if (params.action) query.set('action', params.action);
    if (params.accepted) query.set('accepted', 'true');
    if (params.symbol) query.set('symbol', params.symbol);
    return request<{ decisions: Decision[] }>(`/dashboard/decisions?${query.toString()}`);
  },

  // --- streams ---
  createStream: (input: {
    name: string;
    kind: StreamKind;
    model?: string | null;
    fundingGbp?: number;
    colour?: string;
    symbols?: string[] | null;
  }) => post<{ stream: unknown }>('/streams', input),
  updateStream: (
    id: number,
    input: {
      name?: string;
      model?: string | null;
      enabled?: boolean;
      colour?: string;
      halted?: boolean;
      symbols?: string[] | null;
    },
  ) => request<{ stream: unknown }>(`/streams/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  deleteStream: (id: number) => request<{ ok: boolean }>(`/streams/${id}`, { method: 'DELETE' }),
  deposit: (id: number, amountGbp: number) => post<{ stream: unknown }>(`/streams/${id}/deposit`, { amountGbp }),
  withdraw: (id: number, amountGbp: number) =>
    post<{ stream: unknown }>(`/streams/${id}/withdraw`, { amountGbp }),
  resetStream: (id: number, fundingGbp?: number) =>
    post<{ stream: unknown }>(`/streams/${id}/reset`, fundingGbp === undefined ? {} : { fundingGbp }),
  resumeStream: (id: number) => post<{ stream: unknown }>(`/streams/${id}/resume`),
  addStreamSymbols: (id: number, symbols: string[]) =>
    post<{ stream: unknown }>(`/streams/${id}/symbols`, { symbols }),
  removeStreamSymbol: (id: number, symbol: string) =>
    request<{ stream: unknown }>(`/streams/${id}/symbols/${encodeURIComponent(symbol)}`, { method: 'DELETE' }),

  // --- trades ---
  trades: (params: { streamId?: number; symbol?: string; side?: string; limit?: number; offset?: number } = {}) => {
    const query = new URLSearchParams();
    if (params.streamId) query.set('streamId', String(params.streamId));
    if (params.symbol) query.set('symbol', params.symbol);
    if (params.side) query.set('side', params.side);
    query.set('limit', String(params.limit ?? 100));
    query.set('offset', String(params.offset ?? 0));
    return request<TradesResponse>(`/trades?${query.toString()}`);
  },

  // --- market ---
  quotes: (symbols?: string[]) =>
    request<{ quotes: Quote[]; failures: Array<{ symbol: string; error: string }>; usdToGbp: number | null }>(
      symbols && symbols.length > 0 ? `/market/quotes?symbols=${symbols.join(',')}` : '/market/quotes',
    ),
  providers: () => request<ProvidersResponse>('/market/providers'),
  candleStore: () => request<CandleStoreResponse>('/market/candle-store'),
  universe: () => request<{ universe: UniverseEntry[] }>('/market/universe'),
  analysis: (symbol: string) => request<AnalysisResponse>(`/market/analysis/${encodeURIComponent(symbol)}`),
  chart: (symbol: string, bars: number) =>
    request<ChartResponse>(`/market/chart/${encodeURIComponent(symbol)}?bars=${bars}`),
  crossCheck: (symbol: string) =>
    request<{
      symbol: string;
      quotes: Array<{ provider: string; price: number; currency: string; asOf: string }>;
      errors: Array<{ provider: string; error: string }>;
      maxDivergencePct: number;
      consensusPrice: number | null;
      flagged: boolean;
    }>(`/market/cross-check/${encodeURIComponent(symbol)}`),
  backfill: () =>
    post<{ filled: string[]; skipped: string[]; failed: Array<{ symbol: string; error: string }> }>(
      '/market/backfill',
    ),
  repairHistory: () =>
    post<{
      attempted: string[];
      repaired: string[];
      failed: Array<{ symbol: string; error: string }>;
      skipped: string;
      remainingGaps: number;
    }>('/market/repair-history'),

  // --- models ---
  models: () => request<ModelsResponse>('/models'),
  modelComparison: () => request<{ rows: ModelComparisonRow[] }>('/models/comparison'),
  modelBudget: () => request<ModelBudgetResponse>('/models/budget'),

  // --- engine ---
  engineStatus: () => request<{ scheduler: unknown; lastResult: unknown }>('/engine/status'),
  runCycle: (options: { force?: boolean; streamIds?: number[] } = {}) =>
    post<RunCycleResponse>('/engine/run', options),
  startScheduler: () => post<{ scheduler: unknown }>('/engine/scheduler/start'),
  stopScheduler: () => post<{ scheduler: unknown }>('/engine/scheduler/stop'),

  // --- backtesting ---
  runBacktest: (options: {
    from: string;
    to: string;
    streams: Array<{ name: string; kind: StreamKind; model?: string | null }>;
    fundingGbp?: number;
    cadenceDays?: number;
    label?: string;
    risk?: { allowShorting?: boolean; riskPerTradePct?: number; maxOrderPct?: number };
  }) => post<BacktestRunResponse>('/backtest/run', options),
  runWalkForward: (options: { from: string; to: string; folds?: number; cadenceDays?: number }) =>
    post<WalkForwardResult>('/backtest/walk-forward', options),
  backtestRuns: () => request<{ runs: SavedBacktestRun[] }>('/backtest/runs'),
  backtestRun: (id: number) =>
    request<{ label: string | null; config: unknown; result: BacktestResult }>(`/backtest/runs/${id}`),
  liveSignificance: () =>
    request<{ report: SignificanceReport; streamStats: unknown }>('/backtest/significance'),

  // --- options (model prices) ---
  optionsPresets: () => request<{ presets: OptionPreset[] }>('/options/presets'),
  optionsChain: (symbol: string) =>
    request<OptionChainResponse>(`/options/chain?symbol=${encodeURIComponent(symbol)}`),
  analyseStrategy: (input: {
    symbol: string;
    preset: OptionPresetId;
    expiry?: string;
    backExpiry?: string;
    contracts?: number;
    widthPct?: number;
  }) => post<AnalyseResponse>('/options/analyse', input),
  optionsVolatility: () => request<VolatilityResponse>('/options/volatility'),

  // --- news check ---
  newsOverview: () => request<NewsOverview>('/news/overview'),
  newsSymbol: (symbol: string) => request<NewsSymbolResponse>(`/news/symbol/${encodeURIComponent(symbol)}`),
  runNewsDiscovery: () =>
    post<{ result: { ran: boolean; reason: string; headlinesScanned: number; found: string[]; ignored: string[] } }>(
      '/news/discover',
    ),

  // --- settings ---
  settings: () => request<SettingsResponse>('/settings'),
  saveUniverse: (universe: UniverseEntry[]) =>
    request<{ universe: UniverseEntry[] }>('/settings/universe', {
      method: 'PUT',
      body: JSON.stringify({ universe }),
    }),
  resetUniverse: () => post<{ universe: UniverseEntry[] }>('/settings/universe/reset'),
  clearCache: () => post<{ ok: boolean }>('/settings/cache/clear'),
  resetCircuits: (provider?: string) =>
    post<{ circuits: unknown }>('/settings/circuits/reset', provider ? { provider } : {}),
};

// --- formatting helpers ----------------------------------------------------

const gbpFormatter = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatGbp(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return gbpFormatter.format(value);
}

export function formatPct(value: number | null | undefined, decimals = 2): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${value >= 0 ? '+' : ''}${value.toFixed(decimals)}%`;
}

export function formatNumber(value: number | null | undefined, decimals = 2): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return value.toLocaleString('en-GB', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function formatQuantity(value: number): string {
  // Show whole numbers plainly; fractional shares need the precision.
  return Number.isInteger(value) ? String(value) : value.toFixed(4);
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'never';
  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h ago`;
  return `${Math.round(seconds / 86400)}d ago`;
}

export function kindLabel(kind: StreamKind): string {
  if (kind === 'MOMENTUM') return 'Momentum';
  if (kind === 'OPTIONS') return 'Options (SPY put-write)';
  if (kind === 'AI') return 'Groq AI';
  if (kind === 'PATTERN') return 'Pattern matching';
  return 'AI + Pattern';
}

/** The chart page for a symbol, optionally opened on one decision or trade. */
export function chartLink(symbol: string, select: { decision?: number; trade?: number } = {}): string {
  const query = new URLSearchParams();
  if (select.decision !== undefined) query.set('decision', String(select.decision));
  if (select.trade !== undefined) query.set('trade', String(select.trade));
  const suffix = query.toString();
  return `/charts/${encodeURIComponent(symbol)}${suffix ? `?${suffix}` : ''}`;
}

/** Kinds that call a Groq model. The others are deterministic and carry none. */
export function kindUsesModel(kind: StreamKind): boolean {
  return kind === 'AI' || kind === 'HYBRID';
}

/** A US-dollar amount, for option premiums and strikes. */
export function formatUsd(value: number | null | undefined, decimals = 2): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const digits = Math.abs(value).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return `${value < 0 ? '-' : ''}$${digits}`;
}
